import { useState, useMemo } from 'react';
import { Box, Chip, Pagination } from '@mui/material';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import { Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { Tooltip, MenuItem, Menu, ListItemIcon, darken } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VerifiedIcon from '@mui/icons-material/Verified';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import {
  sourceTypeIconDict,
  typeIconDict,
  lcwTypeLabels,
  getRankLabel,
  getTypeLabelId,
} from './ExperienceWalletDrawer';
import { EDUCATION_LEVEL_BY_ID } from 'constants/educationLevelOptions';
import { useHistory } from 'react-router-dom';

/* i18n */
import { useIntl } from 'react-intl';

import WorkIcon from '@mui/icons-material/Work';

const Title = ({ title, showArrow, sortDirection }) => (
  <>
    <Typography sx={{ fontWeight: 700, color: 'white', pt: 2, pb: 2 }}>{title}</Typography>
    {showArrow ? (
      sortDirection === 'asc' ? (
        <KeyboardArrowUpIcon sx={{ color: 'white' }} />
      ) : (
        <KeyboardArrowDownIcon sx={{ color: 'white' }} />
      )
    ) : null}
  </>
);
Title.propTypes = {
  title: PropTypes.string,
  showArrow: PropTypes.bool,
  sortDirection: PropTypes.string,
};

function getSortDirection(currentSortBy, newSortBy, currentDirection) {
  if (currentSortBy !== newSortBy) {
    return 'asc';
  }
  if (currentDirection === 'asc') {
    return 'desc';
  }
  return 'asc';
}

export const getVisibleItems = (data, currentPage, itemsPerPage) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, data.length);
  return data.slice(startIndex, endIndex);
};

export default function ExperienceWalletDesktopTable({
  experience,
  setItemToEdit,
  deleteItem,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
}) {
  const intl = useIntl();
  const commontHeaderSxProps = {
    backgroundColor: '#344752',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  };
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = useMemo(() => Math.ceil(experience.length / itemsPerPage), [experience.length, itemsPerPage]);

  const visibleItems = getVisibleItems(experience, totalPages > 1 ? currentPage : 1, itemsPerPage);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '220px 2fr 2fr 3fr 120px 120px 50px',
          boxShadow: '0px 3px 12px rgba(0,0,0,0.1)',
        }}
      >
        <Box
          sx={{ pl: 3, ...commontHeaderSxProps }}
          onClick={() => {
            setSortDirection(getSortDirection(sortBy, 'experience', sortDirection));
            setSortBy('experience');
          }}
        >
          <Title
            title={intl.formatMessage({ id: 'experience.table.experience' })}
            showArrow={sortBy === 'experience'}
            sortDirection={sortDirection}
          />
        </Box>
        <Box
          sx={{ ...commontHeaderSxProps }}
          onClick={() => {
            setSortDirection(getSortDirection(sortBy, 'description', sortDirection));
            setSortBy('description');
          }}
        >
          <Title
            title={intl.formatMessage({ id: 'experience.table.description' })}
            showArrow={sortBy === 'description'}
            sortDirection={sortDirection}
          />
        </Box>
        <Box
          sx={{ ...commontHeaderSxProps }}
          onClick={() => {
            setSortDirection(getSortDirection(sortBy, 'provider', sortDirection));
            setSortBy('provider');
          }}
        >
          <Title
            title={intl.formatMessage({ id: 'experience.table.provider' })}
            showArrow={sortBy === 'provider'}
            sortDirection={sortDirection}
          />
        </Box>
        <Box
          sx={{ ...commontHeaderSxProps }}
          onClick={() => {
            setSortDirection(getSortDirection(sortBy, 'numberSkills', sortDirection));
            setSortBy('numberSkills');
          }}
        >
          <Title
            title={intl.formatMessage({ id: 'experience.table.assocSkills' })}
            showArrow={sortBy === 'numberSkills'}
            sortDirection={sortDirection}
          />
        </Box>
        <Box
          sx={{ ...commontHeaderSxProps }}
          onClick={() => {
            setSortDirection(getSortDirection(sortBy, 'start_date', sortDirection));
            setSortBy('start_date');
          }}
        >
          <Title
            title={intl.formatMessage({ id: 'experience.table.start' })}
            showArrow={sortBy === 'start_date'}
            sortDirection={sortDirection}
          />
        </Box>
        <Box
          sx={{ ...commontHeaderSxProps, gridColumn: 'span 2' }}
          onClick={() => {
            setSortDirection(getSortDirection(sortBy, 'end_date', sortDirection));
            setSortBy('end_date');
          }}
        >
          <Title
            title={intl.formatMessage({ id: 'experience.table.end' })}
            showArrow={sortBy === 'end_date' || sortBy === 'default'}
            sortDirection={sortDirection}
          />
        </Box>
        {visibleItems.map((item, index) => (
          <ExperienceWalletDesktopRow
            item={item}
            key={item.id || `row-${index}`}
            grey={index % 2 === 0}
            index={index + (currentPage - 1) * itemsPerPage}
            setItemToEdit={setItemToEdit}
            deleteItem={deleteItem}
          />
        ))}
      </Box>
      <Box mt={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, value) => setCurrentPage(value)}
            showFirstButton
            showLastButton
            shape="rounded"
          />
        )}
      </Box>
    </>
  );
}
ExperienceWalletDesktopTable.propTypes = {
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  setSortBy: PropTypes.func,
  setSortDirection: PropTypes.func,
  setItemToEdit: PropTypes.func,
  deleteItem: PropTypes.func,
  experience: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      field: PropTypes.object,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      description: PropTypes.string,
      skills: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          slice: PropTypes.func,
        })
      ),
    })
  ),
};
function ExperienceWalletDesktopRow({ item, index, grey, setItemToEdit, deleteItem }) {
  const RowIcon = typeIconDict[item.type] || sourceTypeIconDict[item.sourceType] || WorkIcon;
  const intl = useIntl();
  const history = useHistory();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const bgcolor = () => (grey ? '#e4e4e4' : null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // For degree items, show the specific education level (resolved from the stored
  // numeric id) instead of the generic "Education Degree" label.
  const degreeLevelOption =
    item.type === 'degree' && item.level != null ? EDUCATION_LEVEL_BY_ID.get(Number(item.level)) : null;

  return (
    <>
      {/* Experience Type column */}
      <Box sx={{ bgcolor, display: 'flex', flexDirection: 'column', justifyContent: 'center', pl: 3, py: 3 }}>
        {item.wallet ? (
          <Chip
            icon={<VerifiedIcon sx={{ fontSize: '14px !important' }} />}
            label="Verified"
            size="small"
            sx={{
              mb: 0.5,
              alignSelf: 'flex-start',
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              fontWeight: 700,
              fontSize: '0.7rem',
              height: 20,
            }}
          />
        ) : null}
        <Box sx={{ bgcolor, display: 'flex', alignItems: 'center' }}>
          <RowIcon sx={{ color: 'secondary.dark', fontSize: 24, mr: 0.7 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ position: 'relative', top: item.wallet ? 3 : 0 }}>
              <strong>
                {getRankLabel(item) ||
                  (degreeLevelOption
                    ? intl.formatMessage({ id: degreeLevelOption.labelId })
                    : getTypeLabelId(item)
                    ? intl.formatMessage({ id: getTypeLabelId(item) })
                    : lcwTypeLabels[item.type] ?? item.type ?? '')}
              </strong>{' '}
              {item.wallet ? (
                <AccountBalanceWalletIcon sx={{ fontSize: 16, position: 'relative', top: 4, color: 'primary.dark' }} />
              ) : null}
            </Typography>
            {item.wallet ? (
              <Typography sx={{ color: 'primary.main', position: 'relative', top: -2 }} variant="caption">
                {intl.formatMessage({ id: 'experience.fromWallet' })}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Box>
      {/* Description column */}
      <Box sx={{ bgcolor, display: 'flex', alignItems: 'center', px: 1 }}>
        <Tooltip title={item.title?.length > 40 ? item.title : ''}>
          <span>
            {item.title ? item.title.slice(0, 40) : ''}
            {item.title?.length > 40 ? '…' : ''}
          </span>
        </Tooltip>
      </Box>
      {/* Employer/Provider column */}
      <Box sx={{ bgcolor, display: 'flex', alignItems: 'center', px: 1 }}>{item.employer_provider}</Box>
      {/* Skills column */}
      <Box sx={{ bgcolor, display: 'flex', alignItems: 'center', pr: 1, gap: 0.5, flexWrap: 'wrap', py: 1 }}>
        {item.skills &&
          item.skills?.length > 0 &&
          item.skills
            .slice(0, expandedSkills ? item.skills.length : 2)
            .map((skill, skillIdx) => (
              <Chip
                label={skill.name}
                icon={skill.wallet ? <AccountBalanceWalletIcon /> : null}
                color={skill.wallet ? 'primary' : 'secondary'}
                key={skill.id || `skill-${skillIdx}`}
                size="small"
                sx={{ '& .MuiChip-icon': { fontSize: 14 }, maxWidth: 170 }}
              />
            ))}
        {item.skills?.length > 2 && !expandedSkills ? (
          <Typography
            component="span"
            sx={{
              fontSize: 14,
              ml: 0.3,
              cursor: 'pointer',
              color: (theme) => darken(theme.palette.primary.dark, 0.2),
              position: 'relative',
            }}
            onClick={() => setExpandedSkills(true)}
          >
            +{item.skills?.length - 2}
          </Typography>
        ) : null}
      </Box>
      {/* Start date column */}
      <Box sx={{ bgcolor, display: 'flex', alignItems: 'center' }}>
        {item.start_date ? format(new Date(`${item.start_date}T00:00:00`), 'MM-yyyy') : ''}
      </Box>
      {/* End date column */}
      <Box sx={{ bgcolor, display: 'flex', alignItems: 'center' }}>
        {item.end_date
          ? format(new Date(`${item.end_date}T00:00:00`), 'MM-yyyy')
          : intl.formatMessage({ id: 'experience.present' })}
      </Box>
      <Box sx={{ bgcolor, display: 'flex', alignItems: 'center' }}>
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
            <Tooltip title={intl.formatMessage({ id: 'experience.row.actions' })}>
              <IconButton
                onClick={handleClick}
                size="small"
                aria-controls={open ? 'row-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                color="secondary.dark"
              >
                <MoreVertIcon sx={{ width: 24, height: 24 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={anchorEl}
            id="row-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {item.isJst && item.sourceType !== 'military' ? (
              <MenuItem onClick={() => history.push(`${urlPrefix}/jst`)} id={`edit-jst-experience-button-${index}`}>
                <ListItemIcon>
                  <EditIcon fontSize="small" sx={{ color: 'secondary.dark' }} />
                </ListItemIcon>
                {intl.formatMessage({ id: 'experience.row.edit' })}
              </MenuItem>
            ) : item.wallet ? (
              [
                <MenuItem key="edit" onClick={() => setItemToEdit(index)} id={`edit-experience-button-${index}`}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" sx={{ color: 'secondary.dark' }} />
                  </ListItemIcon>
                  {intl.formatMessage({ id: 'experience.row.edit' })}
                </MenuItem>,
                <MenuItem key="delete" onClick={() => deleteItem(index)} id={`delete-experience-button-${index}`}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  {intl.formatMessage({ id: 'experience.row.delete' })}
                </MenuItem>,
              ]
            ) : (
              [
                <MenuItem key="edit" onClick={() => setItemToEdit(index)} id={`edit-experience-button-${index}`}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" sx={{ color: 'secondary.dark' }} />
                  </ListItemIcon>
                  {intl.formatMessage({ id: 'experience.row.edit' })}
                </MenuItem>,
                <MenuItem key="delete" onClick={() => deleteItem(index)} id={`delete-experience-button-${index}`}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  {intl.formatMessage({ id: 'experience.row.delete' })}
                </MenuItem>,
              ]
            )}
          </Menu>
        </>
      </Box>
    </>
  );
}
ExperienceWalletDesktopRow.propTypes = {
  setItemToEdit: PropTypes.func,
  deleteItem: PropTypes.func,
  grey: PropTypes.bool,
  index: PropTypes.number,
  item: PropTypes.shape({
    wallet: PropTypes.bool,
    isJst: PropTypes.bool,
    id: PropTypes.string,
    type: PropTypes.string,
    level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceType: PropTypes.string,
    rank: PropTypes.string,
    field: PropTypes.object,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    title: PropTypes.string,
    employer_provider: PropTypes.string,
    skills: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slice: PropTypes.func,
      })
    ),
  }),
};
